import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import SearchInput from '../../../../component/common/Input/SearchInput';
import SelectMenu from '../../../../component/common/SelectMenu';
import Breadcrumb from '../../../../component/common/Breadcrumb';
import Loader from '../../../../component/common/Loader';
import Topbar from '../../../PulseSurvey/Topbar';
import { CompanyApi } from '../../../../api/companyApi';
import { errorToast, getLocalStorageItem, useOutsideClick } from '../../../../utils/helper';
import { gendersCategory } from '../../../../utils/constants';
import ProfilePic from '../../../../assets/images/dummy_profile.png';
import UsersIcon from '../../../../assets/images/Users.svg';
import BadgeIcon from '../../../../assets/svg/badges/badge.svg';
import StarIcon from '../../../../assets/images/Ratingstar.svg';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

function CBadges() {
  const navigate = useNavigate();
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;

  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [clickedBadges, setClickedBadges] = useState([]);
  const [clickedRowId, setClickedRowId] = useState(null);
  const [clickedUserId, setClickedUserId] = useState(null);

  const [stats, setStats] = useState({ totalEmployees: 0, totalBadges: 0, badgesWon: 0 });
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const filterRef = useRef(null);

  useOutsideClick(filterRef, () => setShowFilterModal(false));

  const pages = [{ name: 'Badges', href: `/${slug}/badges`, current: true }];

  // Filter dropdown options (SelectMenu expects { name, value } items).
  const departmentFilterOptions = [
    { name: 'All Departments', value: '' },
    ...departments.map((dept) => ({ name: dept, value: dept }))
  ];
  const genderFilterOptions = [
    { name: 'All Genders', value: '' },
    ...gendersCategory.filter((g) => g.value !== 6).map((g) => ({ name: g.name, value: g.value }))
  ];

  const activeFilterCount = (departmentFilter !== '' ? 1 : 0) + (genderFilter !== '' ? 1 : 0);

  const resetFilters = () => {
    setCurrentPage(1);
    setDepartmentFilter('');
    setGenderFilter('');
  };

  const statCards = [
    { label: 'Total Employees', value: stats.totalEmployees, icon: UsersIcon },
    { label: 'Total Badges', value: stats.totalBadges, icon: BadgeIcon },
    { label: 'Badges Won', value: stats.badgesWon, icon: StarIcon }
  ];

  const handleShowBadges = (rowData) => {
    setClickedRowId(rowData?._id);
    // Rows come from companyusers; the actual user id is `userId`.
    setClickedUserId(rowData?.userId || rowData?._id);
    // The 'profile' cell renders rowData.name, so expose the badge title as name.
    const badges = (rowData?.badges || []).map((badge) => ({
      ...badge,
      name: badge.title
    }));
    setClickedBadges(badges);
  };

  const fetchData = useCallback(async (page, keyword, department, gender) => {
    setLoader(true);
    try {
      const response = await CompanyApi.getCompanyEmployeesBadges(
        page,
        keyword,
        PER_PAGE,
        department,
        gender
      );
      if (response?.data?.status === 'success') {
        setRows(response.data.data || []);
        setTotalCount(response.data.total || 0);
      } else {
        setRows([]);
        setTotalCount(0);
        errorToast(response?.data?.message || 'Something went wrong');
      }
    } catch (error) {
      setRows([]);
      setTotalCount(0);
      errorToast('An unexpected error occurred.');
      console.error('CBadges fetchData error:', error);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm, departmentFilter, genderFilter);
    }, 200);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, departmentFilter, genderFilter]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await CompanyApi.getCompanyBadgeStats();
        if (response?.data?.status === 'success') {
          const data = response.data.data || {};
          setStats({
            totalEmployees: data.totalEmployees || 0,
            totalBadges: data.totalBadges || 0,
            badgesWon: data.badgesWon || 0
          });
          setDepartments(data.departments || []);
        }
      } catch (error) {
        console.error('CBadges fetchStats error:', error);
      }
    };
    fetchStats();
  }, []);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    { title: 'Team / Department', key: 'teamName', sortable: false, align: 'left', type: 'text3' },
    { title: 'Badges Won', key: 'badgeCount', sortable: false, align: 'center', type: 'number' },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: handleShowBadges }
  ];

  const openBadgeProgress = (badge) => {
    if (!clickedUserId || !badge?._id) return;
    navigate(`/${slug}/badges/progress?userId=${clickedUserId}&badgeId=${badge._id}`);
  };

  const showSidePanel = clickedBadges?.length > 0;

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>B2B | Badges</title>
      </Helmet>
      <Topbar title={'Badges'} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />
      {loader ? <Loader /> : null}

      {/* Headline badge stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray rounded-2xl p-5 flex items-center gap-4"
          >
            <div className="w-14 h-14 flex-shrink-0 rounded-full bg-[#F1F1F1] dark:bg-shoorah-darkBgColor flex items-center justify-center">
              <img src={card.icon} alt={card.label} className="w-7 h-7 object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-gray-500 dark:text-shoorah-darkBg">{card.label}</span>
              <span className="text-2xl font-semibold text-shoorah-lightBlack dark:text-white">
                {card.value ?? 0}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="sm:flex flex-col md:flex-row justify-between items-center gap-3 mt-6">
        <h1 className="font-medium leading-9 text-shoorah-lightBlack dark:text-white text-2xl lg:block hidden">
          Employee Badges
        </h1>
        <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
          <SearchInput
            id="searchKey"
            name="searchKey"
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchTerm(e.target.value);
            }}
            placeholder="Search"
            className="w-full sm:w-auto px-3 py-2"
          />

          {/* Filter button + dropdown panel */}
          <div className="relative w-full sm:w-auto" ref={filterRef}>
            <button
              type="button"
              onClick={() => setShowFilterModal((state) => !state)}
              className="btn-primary w-full sm:w-auto"
            >
              <FunnelIcon className="text-white w-5 h-5" />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-shoorah-primary text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <Transition
              show={showFilterModal}
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="absolute p-5 right-0 top-full z-30 mt-2 w-[100%] sm:w-[420px] dark:bg-shoorah-darkBgTabColor dark:text-white origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="flex items-center justify-between">
                  <p className="font-medium">Filters</p>
                  <div className="flex space-x-3">
                    <button
                      onClick={resetFilters}
                      className="btn-secondary btn-sm"
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="btn-secondary btn-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 mt-4">
                  <SelectMenu
                    menuList={departmentFilterOptions}
                    label="Department"
                    isRequired={false}
                    defaultSelected={departmentFilterOptions.find(
                      (option) => String(option.value) === String(departmentFilter)
                    )}
                    setSelectedMenu={(data) => {
                      setCurrentPage(1);
                      setDepartmentFilter(data?.value ?? '');
                    }}
                  />

                  <SelectMenu
                    menuList={genderFilterOptions}
                    label="Gender"
                    isRequired={false}
                    defaultSelected={genderFilterOptions.find(
                      (option) => String(option.value) === String(genderFilter)
                    )}
                    setSelectedMenu={(data) => {
                      setCurrentPage(1);
                      setGenderFilter(data?.value ?? '');
                    }}
                  />
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 mt-5 gap-4">
        <div className={`col-span-12 ${showSidePanel ? 'md:col-span-9' : ''}`}>
          <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
            columns={columns}
            data={rows}
            name={'company_employee_badges'}
            bottomBorder={totalCount > PER_PAGE}
            loader={loader}
            activeRowId={clickedRowId}
          />
        </TableCard>
        </div>
        {showSidePanel && (
          <div className="col-span-12 md:col-span-3 h-full">
            <div className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray rounded-2xl p-4">
              <h4 className="font-medium text-shoorah-lightBlack dark:text-white mb-3">Badges Won</h4>
              {clickedBadges.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-shoorah-darkBg">No badges yet.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {clickedBadges.map((badge) => (
                    <li
                      key={badge._id}
                      onClick={() => openBadgeProgress(badge)}
                      className="flex items-center justify-between gap-3 p-2 rounded-xl cursor-pointer hover:bg-[#EEEEEE] dark:hover:bg-shoorah-darkBgColor"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={badge.image || ProfilePic}
                          onError={(e) => {
                            e.target.src = ProfilePic;
                          }}
                          alt="badge"
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                        <span className="truncate text-sm text-shoorah-lightBlack dark:text-white">
                          {badge.title}
                        </span>
                      </div>
                      <ChevronRightIcon className="w-5 h-5 text-[#0B0B0B] dark:text-white flex-shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5">
        
      </div>
    </div>
  );
}

export default CBadges;
